import type { Job } from "@malfak/contracts";
import { CircleAlert, CircleCheck, CircleSlash, History } from "lucide-react";
import { useState } from "react";
import { Segmented } from "@/components/ui/controls";
import { Num } from "@/components/ui/misc";
import { Sheet } from "@/components/ui/overlays";
import { ListSkeleton } from "@/components/ui/states";
import { useMe } from "@/hooks/data";
import { useApiQuery } from "@/lib/api/hooks";
import { isPlatformAdmin } from "@/lib/auth";
import { fmtDateTime, fmtNumber } from "@/lib/format";
import { useT } from "@/lib/i18n";
import { cn } from "@/lib/utils";
import { JobProgress } from "./job-progress";
import { AiJobResult, isAiJob } from "./ai-result";
import { jobLabel } from "./model";
import { useJob } from "./use-jobs";

type ItemFilter = "all" | "failed" | "ok" | "skipped";

/** Job detail: live progress, timeline, result and per-item outcomes (failed first). */
export function JobDrawer({ jobId, onClose }: { jobId: string | null; onClose: () => void }) {
  const { t } = useT();
  const { data: job } = useJob(jobId ?? "", { enabled: !!jobId });
  const open = !!jobId;
  return (
    <Sheet open={open} onOpenChange={(v) => !v && onClose()} title={job && jobId ? jobLabel(job, t) : t("jobs.actions.details")} width="md">
      {jobId && (
        <div className="space-y-5" data-testid="job-drawer">
          <JobProgress jobId={jobId} />
          {job && <Meta job={job} />}
          {jobId && <Items jobId={jobId} failed={job?.status === "failed"} />}
        </div>
      )}
    </Sheet>
  );
}

function Meta({ job }: { job: Job }) {
  const { t } = useT();
  const rows: [string, string | null][] = [
    [t("jobs.drawer.created"), fmtDateTime(job.createdAt)],
    [t("jobs.drawer.started"), job.startedAt ? fmtDateTime(job.startedAt) : null],
    [t("jobs.drawer.finished"), job.finishedAt ? fmtDateTime(job.finishedAt) : null],
  ];
  const result = !isAiJob(job) && job.result && typeof job.result === "object" ? Object.entries(job.result as Record<string, unknown>).filter(([, v]) => typeof v === "number" || typeof v === "boolean" || typeof v === "string") : [];
  return (
    <div className="space-y-3">
      <dl className="grid grid-cols-3 gap-2 text-[12px]">
        {rows.map(([k, v]) => (
          <div key={k} className="rounded-xl bg-surface-2 px-3 py-2">
            <dt className="text-ink-3">{k}</dt>
            <dd className="mt-0.5 truncate font-medium text-ink">{v ?? "—"}</dd>
          </div>
        ))}
      </dl>
      {job.retryOf && (
        <p className="flex items-center gap-1.5 text-[12px] text-ink-3">
          <History className="size-3.5" aria-hidden /> {t("jobs.drawer.retryOf")}
        </p>
      )}
      <AiJobResult job={job} />
      {result.length > 0 && (
        <div>
          <h3 className="mb-1.5 text-[12.5px] font-semibold text-ink">{t("jobs.drawer.result")}</h3>
          <dl className="flex flex-wrap gap-1.5">
            {result.map(([k, v]) => (
              <div key={k} className="inline-flex items-center gap-1.5 rounded-lg border border-line px-2 py-1 text-[11.5px]">
                <dt className="text-ink-3">{k}</dt>
                <dd className="font-semibold text-ink">
                  <Num>{typeof v === "number" ? fmtNumber(v) : String(v)}</Num>
                </dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}

function Items({ jobId, failed }: { jobId: string; failed?: boolean }) {
  const { t } = useT();
  const staff = isPlatformAdmin(useMe().data); // customers read "Item 3", staff the raw reference
  const [filter, setFilter] = useState<ItemFilter>("all");
  const { data, isLoading } = useApiQuery("jobItems", { params: { jobId }, query: { limit: 100, ...(filter !== "all" ? { status: filter } : {}) } }, { staleTime: 5_000 });
  const items = data?.items ?? [];
  const icon = { failed: CircleAlert, ok: CircleCheck, skipped: CircleSlash } as const;
  return (
    <section aria-labelledby="job-items-title">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <h3 id="job-items-title" className="text-[13px] font-semibold text-ink">
          {t("jobs.drawer.items")}
        </h3>
        <span className="text-[11.5px] text-ink-3">{t("jobs.drawer.failedFirst")}</span>
        <Segmented
          size="sm"
          className="ms-auto"
          label={t("jobs.drawer.items")}
          value={filter}
          onChange={setFilter}
          options={[
            { value: "all", label: t("jobs.drawer.filterAll") },
            { value: "failed", label: t("jobs.drawer.filterFailed") },
            { value: "ok", label: t("jobs.drawer.filterOk") },
            { value: "skipped", label: t("jobs.drawer.filterSkipped") },
          ]}
        />
      </div>
      {isLoading ? (
        <ListSkeleton rows={4} avatar={false} />
      ) : !items.length ? (
        <p className="rounded-xl bg-surface-2 px-3 py-4 text-center text-[12.5px] text-ink-3" data-testid="job-no-items">{t(failed && filter === "all" ? "jobs.drawer.noItemsFailed" : "jobs.drawer.noItems")}</p>
      ) : (
        <ul className="divide-y divide-line overflow-hidden rounded-xl border border-line" data-testid="job-items">
          {items.map((it, i) => {
            const Icon = icon[it.status];
            return (
              <li key={it.ref} className="flex items-start gap-2.5 px-3 py-2 text-[12px]">
                <Icon className={cn("mt-0.5 size-4 shrink-0", it.status === "failed" ? "text-bad" : it.status === "ok" ? "text-ok" : "text-ink-3")} aria-label={t(`jobs.drawer.filter${it.status === "ok" ? "Ok" : it.status === "failed" ? "Failed" : "Skipped"}`)} />
                <div className="min-w-0 flex-1">
                  {staff ? (
                    <code dir="ltr" className="block truncate font-mono text-[11.5px] text-ink-2">
                      {it.ref}
                    </code>
                  ) : (
                    <span className="block truncate text-[12px] text-ink-2">{t("jobs.drawer.item", { n: i + 1 })}</span>
                  )}
                  {it.error ? <p className="mt-0.5 text-bad">{it.error}</p> : it.status === "failed" && <p className="mt-0.5 text-bad">{t("jobs.drawer.itemFailedGeneric")}</p>}
                </div>
                {it.attempts > 1 && <span className="shrink-0 text-ink-3">{t("jobs.drawer.attempts", { count: it.attempts })}</span>}
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
